import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  EventNote as EventNoteIcon,
  Build as BuildIcon,
  Inventory as InventoryIcon,
  People as PeopleIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  ListAlt as ListAltIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import logo from '../assets/images/logo.svg';
import { publicService } from '../services/api';
const drawerWidth = 240;

// Favicon güncelleme fonksiyonu
function updateFavicon(url) {
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = url;
}

const AdminLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [userRole, setUserRole] = useState(null);
  const [settingsLogo, setSettingsLogo] = useState(null);
  const [settingsFavicon, setSettingsFavicon] = useState(null);
  const [settingsTitle, setSettingsTitle] = useState('Yönetici Paneli');
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const role = localStorage.getItem('userRole');
    setUserRole(role);
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await publicService.getSettings();
        const data = response.data.data;
        setSettingsLogo(data?.homepage_logo || null);
        setSettingsFavicon(data?.homepage_favicon || null);
        setSettingsTitle(data?.homepage_title || 'Yönetici Paneli');
        setSettings(response.data.data);
      } catch (e) {
        setSettingsLogo(null);
        setSettingsFavicon(null);
        setSettingsTitle('Yönetici Paneli');
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    if (settingsFavicon) {
      const faviconUrl = settingsFavicon.includes('?')
        ? `${settingsFavicon}&v=${new Date().getTime()}`
        : `${settingsFavicon}?v=${new Date().getTime()}`;
      updateFavicon(faviconUrl);
    } else {
      updateFavicon('/favicon.ico');
    }
  }, [settingsFavicon]);

  useEffect(() => {
    document.title = settingsTitle;
  }, [settingsTitle]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userId');
    navigate('/admin/login');
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin/dashboard' },
    { text: 'Randevular', icon: <EventNoteIcon />, path: '/admin/appointments' },
    { text: 'Raporlar', icon: <AssignmentIcon />, path: '/admin/reports' },
    ...(userRole === 'admin' ? [
      { text: 'Kullanıcılar', icon: <PeopleIcon />, path: '/admin/users' },
      { text: 'Ustalar', icon: <PeopleIcon />, path: '/admin/masters' },
      { text: 'Kategoriler', icon: <ListAltIcon />, path: '/admin/categories' },
      { text: 'Hizmetler', icon: <BuildIcon />, path: '/admin/services' },
      { text: 'Envanterler', icon: <InventoryIcon />, path: '/admin/inventory' },
      { text: 'Sistem Ayarları', icon: <SettingsIcon />, path: '/admin/settings' }
    ] : [])
  ];

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
      {/* Sadece masaüstünde başlık/logo göster */}
      <Toolbar sx={{ display: { xs: 'none', sm: 'flex' }, justifyContent: 'center', bgcolor: 'primary.main', color: 'white', mb: 1, minHeight: 64 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            justifyContent: 'flex-start',
            px: 1,
            py: 1,
          }}
        >
          <img
            src={settingsLogo || logo}
            alt="ASO"
            style={{
              width: 48,
              height: 48,
              marginRight: 16,
              objectFit: 'contain'
            }}
          />
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 700,
              letterSpacing: 1,
              color: 'white',
              fontSize: 20,
              whiteSpace: 'normal', // Taşarsa alt satıra geçsin
              wordBreak: 'break-word',
              lineHeight: 1.2,
              flex: 1
            }}
          >
            Yönetici Paneli
          </Typography>
        </Box>
      </Toolbar>
      <Divider sx={{ mb: 1, bgcolor: 'primary.dark', opacity: 0.2 }} />
      <List sx={{ flex: 1, px: 1 }}>
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem
              button
              key={item.text}
              onClick={() => navigate(item.path)}
              sx={{
                borderRadius: 2,
                mx: 0.5,
                mb: 0.5,
                color: isActive ? 'primary.main' : 'text.primary',
                bgcolor: isActive ? 'primary.50' : 'transparent',
                fontWeight: isActive ? 700 : 500,
                boxShadow: isActive ? 2 : 0,
                transition: 'background 0.2s, color 0.2s',
                '&:hover': {
                  bgcolor: 'primary.100',
                  color: 'primary.main',
                  boxShadow: 1
                },
                minHeight: 48,
                pl: 2
              }}
            >
              <ListItemIcon sx={{ color: isActive ? 'primary.main' : 'inherit', minWidth: 36 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} sx={{ '.MuiListItemText-primary': { fontWeight: isActive ? 700 : 500, fontSize: 16 } }} />
            </ListItem>
          );
        })}
      </List>
      <Box sx={{ p: 2, textAlign: 'center', color: 'grey.500', fontSize: 13, mt: 'auto', bgcolor: 'background.default', borderTop: 1, borderColor: 'divider' }}>
        {settings?.homepage_footer_copyright || '© ' + new Date().getFullYear() + ' ' + settings?.business_name || 'ASO'}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', bgcolor: 'background.default', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        color="primary"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          boxShadow: 2
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 700, letterSpacing: 1 }}>
            <img src={settingsLogo || logo} alt="ASO" style={{ width: '55px', marginLeft: '10px', marginTop: '10px' }} />
          </Typography>
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.900' }}>A</Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
          >
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Çıkış Yap
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        {/* Mobil Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: 'background.paper',
              borderRight: 0,
              boxShadow: 4
            }
          }}
        >

            
          {/* Drawer üst kısmı: logo ve kapat butonu */}
          <Toolbar sx={{ justifyContent: 'space-between', bgcolor: 'primary.main', color: 'white', minHeight: 64 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 18, color: 'white' }}>Yönetici Paneli</Typography>
            </Box>
            <IconButton edge="end" color="inherit" onClick={handleDrawerToggle}>
              <MenuIcon />
            </IconButton>
          </Toolbar>
          {drawer}
        </Drawer>
        {/* Masaüstü Sidebar */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: 'background.paper',
              borderRight: 0,
              boxShadow: 2
            }
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 4 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
          bgcolor: 'background.default',
          minHeight: '100vh'
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default AdminLayout; 